import { useId } from "react";
import { PemField } from "./PemField";
import { SelectDropdown, type SelectOption } from "./SelectDropdown";
import {
  FieldPairAside,
  FieldPairRow,
  FormGroup,
  FormSection,
  HelpText,
  Input,
  Label,
  SectionTitle,
} from "./styles";

const SECURITY_PROTOCOL_OPTIONS: SelectOption<string>[] = [
  { value: "", label: "None (plaintext)" },
  { value: "SASL_SSL", label: "SASL_SSL" },
  { value: "SASL_PLAINTEXT", label: "SASL_PLAINTEXT" },
  { value: "SSL", label: "SSL" },
];

const SASL_MECHANISM_OPTIONS: SelectOption<string>[] = [
  { value: "PLAIN", label: "PLAIN (default)" },
  { value: "SCRAM-SHA-256", label: "SCRAM-SHA-256" },
  { value: "SCRAM-SHA-512", label: "SCRAM-SHA-512" },
];

interface KafkaFieldsProps {
  bootstrapServers: string;
  setBootstrapServers: (value: string) => void;
  groupId: string;
  setGroupId: (value: string) => void;
  securityProtocol: string;
  setSecurityProtocol: (value: string) => void;
  saslMechanism: string;
  setSaslMechanism: (value: string) => void;
  saslUsername: string;
  setSaslUsername: (value: string) => void;
  saslPassword: string;
  setSaslPassword: (value: string) => void;
  sslCaCert: string;
  setSslCaCert: (value: string) => void;
  sslClientCert: string;
  setSslClientCert: (value: string) => void;
  sslClientKey: string;
  setSslClientKey: (value: string) => void;
}

function usesSsl(protocol: string): boolean {
  return protocol === "SSL" || protocol === "SASL_SSL";
}

function SslCertFields({
  sslCaCert,
  setSslCaCert,
  sslClientCert,
  setSslClientCert,
  sslClientKey,
  setSslClientKey,
}: Pick<
  KafkaFieldsProps,
  | "sslCaCert"
  | "setSslCaCert"
  | "sslClientCert"
  | "setSslClientCert"
  | "sslClientKey"
  | "setSslClientKey"
>) {
  const caCertId = useId();
  const clientCertId = useId();
  const clientKeyId = useId();

  return (
    <FormSection>
      <SectionTitle>SSL Certificates</SectionTitle>

      <PemField
        label="CA Certificate (PEM)"
        id={caCertId}
        value={sslCaCert}
        onChange={setSslCaCert}
        accept=".pem,.crt,.cer"
        ariaLabel="Upload CA certificate PEM file"
        placeholder={
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
        }
        helpText="Optional — leave empty to use the system trusted CA bundle"
      />
      <PemField
        label="Client Certificate (PEM)"
        id={clientCertId}
        value={sslClientCert}
        onChange={setSslClientCert}
        accept=".pem,.crt,.cer"
        ariaLabel="Upload client certificate PEM file"
        placeholder={
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
        }
        helpText="Upload a .pem file or paste the client certificate content"
      />
      <PemField
        label="Client Private Key (PEM)"
        id={clientKeyId}
        value={sslClientKey}
        onChange={setSslClientKey}
        accept=".pem,.key"
        ariaLabel="Upload client private key PEM file"
        placeholder={
          "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
        }
        helpText="Upload a .pem file or paste the client private key content"
      />
    </FormSection>
  );
}

function SaslFields({
  saslMechanism,
  setSaslMechanism,
  saslUsername,
  setSaslUsername,
  saslPassword,
  setSaslPassword,
}: Pick<
  KafkaFieldsProps,
  | "saslMechanism"
  | "setSaslMechanism"
  | "saslUsername"
  | "setSaslUsername"
  | "saslPassword"
  | "setSaslPassword"
>) {
  const saslUsernameId = useId();
  const saslPasswordId = useId();

  return (
    <>
      <FormGroup>
        <Label as="span">SASL Mechanism</Label>
        <SelectDropdown
          value={saslMechanism}
          options={SASL_MECHANISM_OPTIONS}
          onChange={setSaslMechanism}
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor={saslUsernameId}>SASL Username*</Label>
        <Input
          id={saslUsernameId}
          type="text"
          value={saslUsername}
          onChange={(e) => setSaslUsername(e.target.value)}
          placeholder="username or env:VAR_NAME"
          required
        />
        <HelpText>Use env:VAR_NAME to read from environment variable</HelpText>
      </FormGroup>

      <FormGroup>
        <Label htmlFor={saslPasswordId}>SASL Password*</Label>
        <Input
          id={saslPasswordId}
          type="password"
          value={saslPassword}
          onChange={(e) => setSaslPassword(e.target.value)}
          placeholder="password or env:VAR_NAME"
          required
        />
        <HelpText>Use env:VAR_NAME to read from environment variable</HelpText>
      </FormGroup>
    </>
  );
}

export function KafkaFields({
  bootstrapServers,
  setBootstrapServers,
  groupId,
  setGroupId,
  securityProtocol,
  setSecurityProtocol,
  saslMechanism,
  setSaslMechanism,
  saslUsername,
  setSaslUsername,
  saslPassword,
  setSaslPassword,
  sslCaCert,
  setSslCaCert,
  sslClientCert,
  setSslClientCert,
  sslClientKey,
  setSslClientKey,
}: KafkaFieldsProps) {
  const bootstrapServersId = useId();
  const groupIdId = useId();

  return (
    <>
      <FormSection>
        <SectionTitle>Kafka configuration</SectionTitle>

        <FormGroup>
          <Label htmlFor={bootstrapServersId}>Bootstrap servers*</Label>
          <Input
            id={bootstrapServersId}
            type="text"
            value={bootstrapServers}
            onChange={(e) => setBootstrapServers(e.target.value)}
            placeholder="localhost:9092"
            required
          />
          <HelpText>Comma-separated list of broker addresses</HelpText>
        </FormGroup>

        {/* Consumer Group ID + Security protocol share one row per Figma
            14044-76598. */}
        <FieldPairRow>
          <FormGroup>
            <Label htmlFor={groupIdId}>Consumer Group ID</Label>
            <Input
              id={groupIdId}
              type="text"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              placeholder="my-consumer-group"
            />
          </FormGroup>

          <FieldPairAside>
            <Label as="span">Security protocol</Label>
            <SelectDropdown
              value={securityProtocol}
              options={SECURITY_PROTOCOL_OPTIONS}
              onChange={setSecurityProtocol}
            />
          </FieldPairAside>
        </FieldPairRow>

        {securityProtocol.startsWith("SASL") && (
          <SaslFields
            saslMechanism={saslMechanism}
            setSaslMechanism={setSaslMechanism}
            saslUsername={saslUsername}
            setSaslUsername={setSaslUsername}
            saslPassword={saslPassword}
            setSaslPassword={setSaslPassword}
          />
        )}
      </FormSection>

      {usesSsl(securityProtocol) && (
        <SslCertFields
          sslCaCert={sslCaCert}
          setSslCaCert={setSslCaCert}
          sslClientCert={sslClientCert}
          setSslClientCert={setSslClientCert}
          sslClientKey={sslClientKey}
          setSslClientKey={setSslClientKey}
        />
      )}
    </>
  );
}
